import { LayoutPage } from '@/components/hoc';
import { useDocumentTitle } from '@/hooks';
import { useRequestSendAdminMessage } from '@/hooks/httpRequest';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useState } from 'react';
import { AudienceCard, ComposerCard, ConfirmModal, MessagesListTab, PreviewModal } from './components';
import { AppScreenEnum, MessageAudienceEnum, MessageLanguageEnum } from '@/enums';
import type { IAdminMessage, IAdminMessageCta, IAdminMessageFormState, IAdminMessageSendPayload } from '@/interfaces';
import { DEFAULT_FORM_CONFIG } from '@/constants';
import { notify } from '@/functions';
import type { UseFormProps } from 'react-hook-form';

type TProps = {
  useLayout?: boolean;
};

const AdminMessagesPage = ({ useLayout = true }: TProps) => {
  useDocumentTitle({ title: 'Messages admin' });

  const defaultValues: IAdminMessageFormState = {
    messageFr: '',
    messageEn: '',
    audience: MessageAudienceEnum.ALL,
    activeLang: MessageLanguageEnum.FR,
    selectedUsers: [],
    excludedUsers: [],
    genderFilter: [],
    religionFilter: [],
    countryFilter: [],
    ageRange: { min: null, max: null },
    cta: { text: { fr: '', en: '' }, appPath: '' },
  };

  const formMethods = useForm<IAdminMessageFormState>({
    ...(DEFAULT_FORM_CONFIG as UseFormProps<IAdminMessageFormState>),
    defaultValues
  });

  const { handleSubmit, trigger, control } = formMethods;

  const activeLang = useWatch({ control, name: 'activeLang' }) ?? MessageLanguageEnum.FR;
  const audience = useWatch({ control, name: 'audience' }) ?? MessageAudienceEnum.ALL;
  const messageFr = useWatch({ control, name: 'messageFr' }) ?? '';
  const messageEn = useWatch({ control, name: 'messageEn' }) ?? '';
  const genderFilter = useWatch({ control, name: 'genderFilter', defaultValue: [] });
  const religionFilter = useWatch({ control, name: 'religionFilter', defaultValue: [] });
  const countryFilter = useWatch({ control, name: 'countryFilter', defaultValue: [] });
  const ageRange = useWatch({ control, name: 'ageRange', defaultValue: { min: null, max: null } });
  const excludedUsers = useWatch({ control, name: 'excludedUsers', defaultValue: [] });
  const cta = useWatch({ control, name: 'cta', defaultValue: { text: { fr: '', en: '' }, appPath: '' } });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const previewText = activeLang === MessageLanguageEnum.FR ? messageFr : messageEn;

  const { triggerSendAdminMessage } = useRequestSendAdminMessage({
    onSuccess: () => {
      notify('Message envoyé.');
      setConfirmOpen(false);
      setPreviewOpen(false);
      formMethods.reset(defaultValues);
    }
  });

  const getErrorMessage = (error: unknown) => {
    if (error && typeof error === 'object') {
      const internalMessage = (error as { internalMessage?: unknown }).internalMessage;
      if (typeof internalMessage === 'string' && internalMessage.length) return internalMessage;
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string' && message.length) return message;
    }
    return undefined;
  };

  const handleConfirmSend = handleSubmit(async data => {
    if (data.audience === MessageAudienceEnum.SPECIFIC && data.selectedUsers.length === 0) {
      notify('Aucun utilisateur sélectionné', 'info');
      return;
    }

    const recipients =
      data.audience === MessageAudienceEnum.SPECIFIC
        ? data.selectedUsers.map(u => u.id)
        : [data.audience];

    const isBroadcast = data.audience !== MessageAudienceEnum.SPECIFIC;
    const payload: IAdminMessageSendPayload = { messageFR: data.messageFr, messageEN: data.messageEn, recipients };

    if (!isBroadcast && data.selectedUsers.length > 0) {
      payload.recipientDetails = data.selectedUsers.map(u => ({ id: u.id, name: u.name }));
    }
    if (isBroadcast) {
      if (data.genderFilter.length > 0) payload.genderFilter = data.genderFilter;
      if (data.religionFilter.length > 0) payload.religionFilter = data.religionFilter;
      if (data.countryFilter.length > 0) payload.countryFilter = data.countryFilter;
      if (data.ageRange.min != null) payload.minAge = data.ageRange.min;
      if (data.ageRange.max != null) payload.maxAge = data.ageRange.max;
      if (data.excludedUsers.length > 0) payload.excludedRecipientDetails = data.excludedUsers.map(u => ({ id: u.id, name: u.name }));
    }

    const { text: ctaText, appPath } = data.cta;
    const hasCtaTextFr = ctaText.fr.trim().length > 0;
    const hasCtaTextEn = ctaText.en.trim().length > 0;
    const hasAppPath = appPath.length > 0;
    const isAnyCtaFieldFilled = hasCtaTextFr || hasCtaTextEn || hasAppPath;

    if (isAnyCtaFieldFilled) {
      if (!hasCtaTextFr || !hasCtaTextEn || !hasAppPath) {
        notify('Si vous ajoutez un CTA, tous les champs (texte FR, texte EN, navigation) sont requis', 'error');
        return;
      }
      payload.cta = { text: { fr: ctaText.fr.trim(), en: ctaText.en.trim() }, navigation: { appPath } };
    }

    try {
      setIsSubmitting(true);
      await triggerSendAdminMessage(payload);
    } catch (e) {
      notify(getErrorMessage(e) || "Erreur lors de l'envoi du message", 'error');
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleOpenConfirm = async () => {
    const isValid = await trigger(['messageFr', 'messageEn']);
    if (!isValid) {
      const hasENError = !!formMethods.formState.errors.messageEn;
      const hasFRError = !!formMethods.formState.errors.messageFr;
      if (activeLang === MessageLanguageEnum.FR && hasENError && !hasFRError) {
        formMethods.setValue('activeLang', MessageLanguageEnum.EN);
      }
      if (activeLang === MessageLanguageEnum.EN && hasFRError && !hasENError) {
        formMethods.setValue('activeLang', MessageLanguageEnum.FR);
      }
      return;
    }
    const ageErrors = (formMethods.formState.errors as any).ageRange;
    if (ageErrors?.min || ageErrors?.max) return;
    setConfirmOpen(true);
  };

  const handleResend = (message: IAdminMessage) => {
    const isBroadcastRecipient =
      message.recipients.length === 1 &&
      Object.values(MessageAudienceEnum).includes(message.recipients[0] as MessageAudienceEnum);

    const ctaValue: IAdminMessageCta = message.cta
      ? {
          text: {
            fr: message.cta.text?.fr ?? '',
            en: message.cta.text?.en ?? ''
          },
          appPath: (message.cta.navigation?.appPath ?? '') as AppScreenEnum | ''
        }
      : { text: { fr: '', en: '' }, appPath: '' };

    formMethods.reset({
      messageFr: message.messageFR,
      messageEn: message.messageEN,
      audience: isBroadcastRecipient ? (message.recipients[0] as MessageAudienceEnum) : MessageAudienceEnum.SPECIFIC,
      activeLang: MessageLanguageEnum.FR,
      selectedUsers: message.recipientDetails ?? [],
      excludedUsers: message.excludedRecipientDetails ?? [],
      genderFilter: message.genderFilter ?? [],
      religionFilter: message.religionFilter ?? [],
      countryFilter: message.countryFilter ?? [],
      ageRange: { min: message.minAge ?? null, max: message.maxAge ?? null },
      cta: ctaValue,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const content = (
    <FormProvider {...formMethods}>
      <div className='grid grid-cols-1 lg:grid-cols-12 gap-4 items-start'>
        <div className='space-y-4 lg:col-span-3'>
          <AudienceCard />
        </div>
        <div className='space-y-4 lg:col-span-5'>
          <ComposerCard onOpenPreview={() => setPreviewOpen(true)} onSend={handleOpenConfirm} />
        </div>
        <div className='lg:col-span-4'>
          <MessagesListTab onResend={handleResend} />
        </div>
      </div>

      <PreviewModal
        open={previewOpen}
        activeLang={activeLang}
        previewText={previewText}
        previewCta={cta.text.fr && cta.text.en && cta.appPath ? cta : undefined}
        onChangeLang={(lang) => formMethods.setValue('activeLang', lang)}
        onClose={() => setPreviewOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        audience={audience}
        excludedUsers={excludedUsers}
        genderFilter={genderFilter}
        religionFilter={religionFilter}
        countryFilter={countryFilter}
        ageRange={ageRange}
        isSubmitting={isSubmitting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSend}
      />
    </FormProvider>
  );

  if (!useLayout) return content;

  return (
    <LayoutPage
      header={{
        title: 'Messages admin',
        description: "Message système (FR/EN) avec ciblage d'audience."
      }}
      breadcrumb={{ title: 'Messages' }}
    >
      {content}
    </LayoutPage>
  );
};

export default AdminMessagesPage;
